<template>
  <div>
    <div
      v-if="!loadingError"
      class="text-right mb-2"
    >
      <b-button
        variant="outline-primary"
        class="refresh-btn mr-1"
        :disabled="loading"
        @click="fetchMatrix"
      >
        Refresh
      </b-button>
    </div>

    <div
      v-if="loading"
      class="text-center"
    >
      <b-spinner
        label="Small Spinner"
        variant="primary"
      />
    </div>

    <b-alert
      variant="danger"
      :show="loadingError ? true : false"
    >
      <div class="alert-body">
        <p>
          {{ loadingError }}
        </p>
      </div>
    </b-alert>

    <supplier-return-details
      v-if="openItemId"
      :item-id="openItemId"
      @modal-closed="openItemId = null"
    />

    <b-modal
      v-model="cellModalShow"
      :title="cellModalTitle"
      size="lg"
      scrollable
      hide-footer
    >
      <div
        v-if="cellItemsLoading"
        class="text-center py-2"
      >
        <b-spinner
          label="Small Spinner"
          variant="primary"
        />
      </div>
      <b-alert
        variant="danger"
        :show="cellItemsError ? true : false"
      >
        <div class="alert-body">
          <p>{{ cellItemsError }}</p>
        </div>
      </b-alert>
      <b-table
        v-if="!cellItemsLoading && !cellItemsError"
        :items="cellItems"
        :fields="cellTableFields"
        responsive="sm"
        striped
        hover
        class="compact-table"
      >
        <template #cell(challan_date)="data">
          {{ data.item.challan_date && formatedDate(data.item.challan_date) }}
        </template>
        <template #cell(status)="data">
          <status-icon :value="data.item.status" />
        </template>
        <template #cell(total_amount)="data">
          {{ formateNumber(data.item.total_amount) }}
        </template>
        <template #cell(actions)="data">
          <feather-icon
            v-b-tooltip.hover="{ boundary: 'window' }"
            title="View Details"
            icon="EyeIcon"
            size="20"
            class="cursor-pointer"
            @click="displayDetails(data.item.id)"
          />
        </template>
      </b-table>
    </b-modal>

    <div v-if="!loading && !loadingError">
      <b-table-simple
        responsive
        striped
        hover
        no-border-collapse
        class="compact-table matrix-table"
      >
        <b-thead head-variant="light">
          <b-tr>
            <b-th
              sticky-column
              class="supplier-col matrix-header-col"
            >
              Supplier
            </b-th>
            <b-th
              v-for="month in months"
              :key="month"
              class="month-col matrix-header-col text-center"
            >
              {{ formatMonthLabel(month) }}
            </b-th>
          </b-tr>
        </b-thead>
        <b-tbody>
          <b-tr
            v-for="row in results"
            :key="row.supplier_id"
          >
            <b-td
              sticky-column
              class="supplier-col font-weight-bold"
              :title="row.identifier"
            >
              {{ row.identifier }}
            </b-td>
            <b-td
              v-for="month in months"
              :key="`${row.supplier_id}-${month}`"
              class="month-col text-center p-0"
            >
              <div
                class="matrix-cell"
                :class="{ 'matrix-cell-empty': !hasCellData(row, month) }"
                @click="openCellItems(row, month)"
              >
                <div
                  v-if="hasCellData(row, month)"
                  class="matrix-cell-statuses"
                >
                  <b-badge
                    v-for="(count, status) in getCellStatusCounts(row, month)"
                    :key="status"
                    v-b-tooltip.hover="{ boundary: 'window' }"
                    :title="`${status}: ${count}`"
                    :variant="statusVariant(status)"
                    class="status-chip"
                  >
                    {{ count }}
                  </b-badge>
                </div>
              </div>
            </b-td>
          </b-tr>
        </b-tbody>
      </b-table-simple>
    </div>

    <detailed-pagination
      v-if="!loading && !loadingError"
      :total-items="totalItems"
      :per-page="perPage"
      :current-page="currentPage"
      @page-changed="pageChangeHandler"
    />
  </div>
</template>

<script>
import {
  VBTooltip,
  BSpinner,
  BAlert,
  BButton,
  BTable,
  BModal,
  BTableSimple,
  BThead,
  BTbody,
  BTr,
  BTh,
  BTd,
  BBadge,
} from 'bootstrap-vue'
import axios from 'axios'
import dateMixin from '@/mixins/dateMixin'
import formatMixin from '@/mixins/formatMixin'
import DetailedPagination from '@/components/UI/DetailedPagination.vue'
import StatusIcon from '@/components/UI/StatusIcon.vue'
import SupplierReturnDetails from '@/components/SupplierReturn/SupplierReturnDetails.vue'

const STATUS_VARIANTS = {
  Open: 'danger',
  'Partially Adjusted': 'warning',
  Adjusted: 'success',
}

function mergeStatusCounts(statusCounts) {
  const merged = {}
  const openCount = statusCounts.Open || 0
  if (openCount) {
    merged.Open = openCount
  }
  const partiallyAdjustedCount = (statusCounts['Partially Adjusted'] || 0)
    + (statusCounts['Partially Closed'] || 0)
    + (statusCounts['Partially Fulfilled'] || 0)
  if (partiallyAdjustedCount) {
    merged['Partially Adjusted'] = partiallyAdjustedCount
  }
  const adjustedCount = (statusCounts.Adjusted || 0)
    + (statusCounts.Closed || 0)
    + (statusCounts.Fulfilled || 0)
  if (adjustedCount) {
    merged.Adjusted = adjustedCount
  }
  return merged
}

function monthBounds(monthKey) {
  const [year, month] = monthKey.split('-').map(Number)
  const lastDay = new Date(year, month, 0).getDate()
  return {
    from: `${monthKey}-01`,
    to: `${monthKey}-${String(lastDay).padStart(2, '0')}`,
  }
}

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BSpinner,
    BAlert,
    BButton,
    BTable,
    BModal,
    BTableSimple,
    BThead,
    BTbody,
    BTr,
    BTh,
    BTd,
    BBadge,
    DetailedPagination,
    StatusIcon,
    SupplierReturnDetails,
  },
  mixins: [dateMixin, formatMixin],
  props: {
    filters: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      loadingError: null,
      results: [],
      months: [],
      totalItems: 0,
      currentPage: 1,
      perPage: 50,
      openItemId: null,
      cellModalShow: false,
      cellModalTitle: '',
      cellItems: [],
      cellItemsLoading: false,
      cellItemsError: null,
      activeCell: null,
      cellTableFields: [
        { key: 'challan_number', label: 'Challan No.' },
        { key: 'challan_date', label: 'Challan Date' },
        { key: 'status', label: 'Status' },
        { key: 'total_items', label: 'Items' },
        { key: 'total_amount', label: 'Amount' },
        { key: 'actions', label: 'Actions' },
      ],
    }
  },
  watch: {
    currentPage() {
      this.fetchMatrix()
    },
    filters() {
      if (this.currentPage !== 1) {
        this.currentPage = 1
      } else {
        this.fetchMatrix()
      }
    },
  },
  created() {
    this.fetchMatrix()
  },
  methods: {
    statusVariant(status) {
      return STATUS_VARIANTS[status] || 'secondary'
    },
    hasCellData(row, month) {
      return this.getCellTotal(row, month) > 0
    },
    formatMonthLabel(monthKey) {
      const [year, month] = monthKey.split('-').map(Number)
      const date = new Date(year, month - 1, 1)
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    },
    getCellTotal(row, month) {
      return row.cells?.[month]?.total || 0
    },
    getCellStatusCounts(row, month) {
      const statusCounts = row.cells?.[month]?.status_counts || {}
      return mergeStatusCounts(statusCounts)
    },
    pageChangeHandler(page) {
      this.currentPage = page
    },
    fetchMatrix() {
      this.loading = true
      axios.post('/returns/supplier_returns_matrix', {
        page: this.currentPage,
        per_page: this.perPage,
        filters: this.filters,
      })
        .then(res => {
          this.results = res.data.results
          this.months = res.data.months
          this.totalItems = res.data.count
          this.loadingError = null
          this.loading = false
        })
        .catch(error => {
          this.loadingError = error?.response?.data?.error
            || error?.response?.data?.detail
            || 'Something went wrong'
          this.loading = false
        })
    },
    openCellItems(row, month) {
      if (!this.hasCellData(row, month)) return

      this.activeCell = { supplierId: row.supplier_id, month, identifier: row.identifier }
      this.cellModalTitle = `${row.identifier} — ${this.formatMonthLabel(month)}`
      this.cellModalShow = true
      this.fetchCellItems()
    },
    fetchCellItems() {
      if (!this.activeCell) return

      const { from, to } = monthBounds(this.activeCell.month)
      let challanFrom = from
      let challanTo = to
      if (this.filters.challan_date_from && challanFrom < this.filters.challan_date_from) {
        challanFrom = this.filters.challan_date_from
      }
      if (this.filters.challan_date_to && challanTo > this.filters.challan_date_to) {
        challanTo = this.filters.challan_date_to
      }

      const cellFilters = {
        ...this.filters,
        suppliers: [this.activeCell.supplierId],
        challan_date_from: challanFrom,
        challan_date_to: challanTo,
      }

      this.cellItemsLoading = true
      this.cellItemsError = null
      axios.post('/returns/filtered_supplier_returns', {
        page: 1,
        per_page: 500,
        sort_by: 'challan_date',
        sort_desc: true,
        filters: cellFilters,
      })
        .then(res => {
          this.cellItems = res.data.results
          this.cellItemsLoading = false
        })
        .catch(error => {
          this.cellItemsError = error?.response?.data?.error || 'Something went wrong'
          this.cellItemsLoading = false
        })
    },
    displayDetails(id) {
      this.cellModalShow = false
      this.openItemId = id
    },
  },
}
</script>

<style scoped>
.matrix-table {
  width: auto;
  max-width: 100%;
}

.supplier-col {
  white-space: nowrap;
  min-width: 320px;
  max-width: 320px;
  width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.08);
}

.matrix-table >>> thead th.matrix-header-col {
  background-color: #f8f9fa !important;
  vertical-align: middle;
}

.matrix-table >>> tbody tr:nth-of-type(odd) > td.supplier-col {
  background-color: #fafafc !important;
}

.matrix-table >>> tbody tr:nth-of-type(even) > td.supplier-col {
  background-color: #fff !important;
}

.matrix-table.table-hover >>> tbody tr:hover > td.supplier-col {
  background-color: #f6f6f9 !important;
}

.month-col {
  min-width: 110px;
}

.matrix-cell {
  min-height: 36px;
  padding: 0.35rem 0.25rem;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.matrix-cell:hover {
  background-color: rgba(115, 103, 240, 0.08);
}

.matrix-cell-empty {
  cursor: default;
}

.matrix-cell-empty:hover {
  background-color: transparent;
}

.matrix-cell-statuses {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px;
}

.status-chip {
  font-size: 0.75rem;
  min-width: 1.5rem;
}

</style>
